import React, { useState, useEffect, useCallback, memo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    CircularProgress,
    Select,
    MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { inputStyles, getInputStyles } from './styles';
import { validateExperience } from './validation';
import { MONTHS, YEAR_RANGE, parseDateString } from './constants';

const ExperienceSection = memo(({ 
    initialData, 
    authAxios, 
    setError, 
    onUpdate 
}) => {
    const [experience, setExperience] = useState(initialData || []);
    const [saving, setSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(true);

    useEffect(() => {
        if (initialData) {
            // Parse dates from API (YYYY-MM-DD) to month/year components
            const parsedData = initialData.map(exp => {
                const startParsed = parseDateString(exp.start_date);
                const endParsed = parseDateString(exp.end_date);
                return {
                    ...exp,
                    start_month: startParsed.month,
                    start_year: startParsed.year,
                    end_month: endParsed.month,
                    end_year: endParsed.year,
                };
            });
            setExperience(parsedData);
        }
    }, [initialData]);

    const addExperience = useCallback(() => {
        setExperience(prev => [...prev, {
            id: Date.now(),
            company: '',
            role: '',
            city: '',
            country: '',
            description: '',
            start_month: '',
            start_year: '',
            end_month: '',
            end_year: '',
        }]);
    }, []);

    const updateExperience = useCallback((id, field, value) => {
        setExperience(prev => prev.map(exp => 
            exp.id === id ? { ...exp, [field]: value } : exp
        ));
    }, []);

    const removeExperience = useCallback((id) => {
        setExperience(prev => prev.filter(exp => exp.id !== id));
    }, []);

    // Format date for API (YYYY-MM-DD)
    const formatDate = (month, year) => {
        if (!month || !year) return null;
        return `${year}-${month.padStart(2, '0')}-01`;
    };

    const saveExperienceEntry = async (exp) => {
        try {
            const payload = {
                company: exp.company,
                role: exp.role || exp.title,
                city: exp.city || '',
                country: exp.country || '',
                description: exp.description || '',
                start_date: formatDate(exp.start_month, exp.start_year),
                end_date: formatDate(exp.end_month, exp.end_year),
            };

            if (exp.id && typeof exp.id === 'number' && exp.id > 1000000000000) {
                const response = await authAxios.post('/experience/', payload);
                return response.data;
            } else {
                const response = await authAxios.patch(`/experience/${exp.id}/`, payload);
                return response.data;
            }
        } catch (error) {
            console.error('Failed to save experience:', error);
            throw error;
        }
    };

    const handleSaveAll = async () => {
        const errors = validateExperience(experience);
        if (errors.length > 0) {
            setShowErrors(true);
            const fieldErrors = errors.map(e => {
                const fields = Object.keys(e).filter(k => k !== 'index');
                return `Entry ${e.index + 1}: ${fields.join(', ')}`;
            });
            setError?.(`Experience validation failed: ${fieldErrors.join('; ')}`);
            return;
        }


        setSaving(true);

        try {
            const savedEntries = await Promise.all(
                experience.map(exp => saveExperienceEntry(exp))
            );
            // Parse dates from response
            const parsedEntries = savedEntries.map(exp => {
                const startParsed = parseDateString(exp.start_date);
                const endParsed = parseDateString(exp.end_date);
                return {
                    ...exp,
                    start_month: startParsed.month,
                    start_year: startParsed.year,
                    end_month: endParsed.month,
                    end_year: endParsed.year,
                };
            });
            setExperience(parsedEntries);
            onUpdate?.(savedEntries);
        } catch (error) {
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to save experience';
            setError?.(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const hasFieldError = (expIndex, field) => {
        if (!showErrors) return false;
        const exp = experience[expIndex];
        if (!exp) return false;
        
        if (field === 'company' && !exp.company?.trim()) return true;
        if (field === 'role' && !(exp.role?.trim() || exp.title?.trim())) return true;
        if (field === 'start_date' && (!exp.start_month || !exp.start_year)) return true;
        return false;
    };

    // Dropdown styles
    const selectStyles = {
        borderRadius: '6px',
        fontSize: '0.875rem',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d1d5db',
        },
    };
    
    const errorSelectStyles = {
        ...selectStyles,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#fca5a5',
            borderWidth: '1.5px',
        },
        backgroundColor: '#fef2f2',
    };

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>
                    Work Experience
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={addExperience}
                        startIcon={<AddIcon />}
                        sx={{
                            color: '#4f46e5',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                        }}
                    >
                        Add Experience
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveAll}
                        disabled={saving || experience.length === 0}
                        sx={{
                            bgcolor: '#4f46e5',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: '6px',
                            px: 3,
                            '&:hover': { bgcolor: '#4338ca' },
                        }}
                    >
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save All'}
                    </Button>
                </Box>
            </Box>

            {experience.length === 0 ? (
                <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    No work experience entries. Click "Add Experience" to add one.
                </Typography>
            ) : (
                experience.map((exp, index) => (
                    <Box key={exp.id} sx={{ 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '8px', 
                        p: 3, 
                        mb: 2,
                        position: 'relative',
                    }}>
                        <IconButton
                            size="small"
                            onClick={() => removeExperience(exp.id)}
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    Company<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Company name"
                                    value={exp.company || ''}
                                    onChange={(e) => updateExperience(exp.id, 'company', e.target.value)}
                                    sx={hasFieldError(index, 'company') ? getInputStyles(true, true) : inputStyles}
                                />
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    Role / Title<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Job title"
                                    value={exp.role || exp.title || ''}
                                    onChange={(e) => updateExperience(exp.id, 'role', e.target.value)}
                                    sx={hasFieldError(index, 'role') ? getInputStyles(true, true) : inputStyles}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    Start Date<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 1 }}>
                                    <Select
                                        size="small"
                                        value={exp.start_month || ''}
                                        onChange={(e) => updateExperience(exp.id, 'start_month', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={hasFieldError(index, 'start_date') ? errorSelectStyles : selectStyles}
                                    >
                                        <MenuItem value="" disabled>Month</MenuItem>
                                        {MONTHS.map(m => (
                                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                        ))}
                                    </Select>
                                    <Select
                                        size="small"
                                        value={exp.start_year || ''}
                                        onChange={(e) => updateExperience(exp.id, 'start_year', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={hasFieldError(index, 'start_date') ? errorSelectStyles : selectStyles}
                                    >
                                        <MenuItem value="" disabled>Year</MenuItem>
                                        {YEAR_RANGE.map(y => (
                                            <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    End Date
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 1 }}>
                                    <Select
                                        size="small"
                                        value={exp.end_month || ''}
                                        onChange={(e) => updateExperience(exp.id, 'end_month', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={selectStyles}
                                    >
                                        <MenuItem value="">Month</MenuItem>
                                        {MONTHS.map(m => (
                                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                        ))}
                                    </Select>
                                    <Select
                                        size="small"
                                        value={exp.end_year || ''}
                                        onChange={(e) => updateExperience(exp.id, 'end_year', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={selectStyles}
                                    >
                                        <MenuItem value="">Year</MenuItem>
                                        {YEAR_RANGE.map(y => (
                                            <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    City
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="City"
                                    value={exp.city || ''}
                                    onChange={(e) => updateExperience(exp.id, 'city', e.target.value)}
                                    sx={inputStyles}
                                />
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    Country
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Country"
                                    value={exp.country || ''}
                                    onChange={(e) => updateExperience(exp.id, 'country', e.target.value)}
                                    sx={inputStyles}
                                />
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                Description
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                multiline
                                rows={3}
                                placeholder="Describe your responsibilities and achievements..."
                                value={exp.description || ''}
                                onChange={(e) => updateExperience(exp.id, 'description', e.target.value)}
                                sx={inputStyles}
                            />
                        </Box>
                    </Box>
                ))
            )}
        </Box>
    );
});

ExperienceSection.displayName = 'ExperienceSection';

export default ExperienceSection;
